import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import { ThemeProvider } from './context/ThemeContext';
import { BrandProvider } from './context/BrandContext';
import { DataCacheProvider } from './context/DataCacheContext';
import KeyboardShortcutsProvider from './components/KeyboardShortcutsProvider';
import posthog from './config/posthog';
import { initPostHog } from './config/posthog';
import { PostHogProvider, useFeatureFlagEnabled } from 'posthog-js/react';
import PostHogPageviewTracker from './components/PostHogPageviewTracker';
import PrivateRoute from './components/PrivateRoute';
import NavbarLayout from './components/layouts/NavbarLayout';
import SidebarLayout from './components/layouts/SidebarLayout';

// Import pages
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import FormList from './pages/FormList';
import FormBuilder from './pages/FormBuilder';
import FormResponses from './pages/FormResponses';
import FormAnalytics from './pages/FormAnalytics';
import PublicForm from './pages/PublicForm';
import PublicPage from './pages/PublicPage';
import ThankYou from './pages/ThankYou';
import SettingsPage from './pages/Settings';
import AcceptInvite from './pages/AcceptInvite';
import DemoPublicForm from './pages/DemoPublicForm';
import EmbedDemo from './pages/EmbedDemo';
import FormPreviewRenderer from './components/FormPreviewRenderer';
// Pages / Sites module
import SitesList from './pages/SitesList';
import SiteDetail from './pages/SiteDetail';
import PageBuilder from './pages/PageBuilder';
// CRM / Data module
import CRMLayout from './pages/CRMLayout';
import CRMObjectList from './pages/CRMObjectList';
import CRMRecordDetail from './pages/CRMRecordDetail';
import CRMPipelines from './pages/CRMPipelines';
import CRMPipelineView from './pages/CRMPipelineView';
import DataObjectSlugResolver from './pages/DataObjectSlugResolver';
import DataObjectIdRedirect from './pages/DataObjectIdRedirect';
import UploadRecords from './pages/able/UploadRecords';
// Chat module
import ChatApp from './pages/ChatApp';
// Admin
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import SuperAdminRoute from './components/SuperAdminRoute';
// Workflow module
import WorkflowList from './pages/WorkflowList';
import WorkflowBuilder from './pages/WorkflowBuilder';
// Chatbot module
import ChatbotList from './pages/ChatbotList';
import ChatbotBuilder from './pages/ChatbotBuilder';
// Prospecting module
import Prospecting from './pages/Prospecting';
import ProspectingWorkspace from './pages/ProspectingWorkspace';
import ProspectingGuidedSetup from './pages/ProspectingGuidedSetup';
import ProspectingSavedListWorkspace from './pages/ProspectingSavedListWorkspace';
import { ProspectingProvider } from './context/ProspectingContext';
import { useBrand } from './context/BrandContext';
import './index.css';

// Initialize PostHog
initPostHog();

// Layout Components
function AuthLayout({ children, skipRedirect = false }) {
  const { isAuthenticated } = useAuth();
  const { brand } = useBrand();
  console.log("isAuthenticated:", isAuthenticated);
  if (isAuthenticated && !skipRedirect) {
    // Navigate to home page with router
    return <Navigate to="/chat" replace />;
  }

  // Able: clean white background — Able pages handle their own layout
  if (brand === 'able') {
    return <div className="min-h-screen bg-white">{children}</div>;
  }

  // FastestForm: gradient + decorative blur circles
  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-50 via-slate-50 to-blue-50 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-40">
        <div className="absolute top-0 left-0 w-72 h-72 bg-gradient-to-br from-brand-200/30 to-transparent rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute top-1/2 right-0 w-96 h-96 bg-gradient-to-bl from-blue-200/20 to-transparent rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 left-1/3 w-80 h-80 bg-gradient-to-tr from-slate-200/20 to-transparent rounded-full blur-3xl transform translate-y-1/2"></div>
      </div>

      {/* Subtle Grid Pattern */}
      <div className="absolute inset-0 opacity-[0.02]" style={{
        backgroundImage: `radial-gradient(circle at 1px 1px, rgb(15 23 42) 1px, transparent 0)`,
        backgroundSize: '24px 24px'
      }}></div>

      <div className="relative z-10">
        {children}
      </div>
    </div>
  );
}

function MainAppLayout({ children }) {
  const { brandConfig } = useBrand();

  if (brandConfig.layout === 'sidebar') {
    return <SidebarLayout>{children}</SidebarLayout>;
  }
  return <NavbarLayout>{children}</NavbarLayout>;
}

function ThankYouLayout({ children }) {
  useAllowOverscroll();
  return (
    <div className="min-h-screen text-gray-900">
      {children}
    </div>
  );
}

function useAllowOverscroll() {
  React.useEffect(() => {
    document.documentElement.style.overscrollBehavior = 'auto';
    document.body.style.overscrollBehavior = 'auto';
    return () => {
      document.documentElement.style.overscrollBehavior = '';
      document.body.style.overscrollBehavior = '';
    };
  }, []);
}

function PublicFormLayout({ children, embed, noConstraints = false }) {
  useAllowOverscroll();

  if (noConstraints) {
    // For pages - no constraints, let content control layout
    return (
      <div className="min-h-screen text-gray-900">
        {children}
      </div>
    );
  }

  // For forms - keep original constraints
  return (
    <div className={`min-h-screen text-gray-900 ${embed ? 'bg-white' : 'bg-gray-50'}`}>
      <main className={`max-w-7xl mx-auto ${!embed ? 'py-6 sm:px-6 lg:px-8' : ''}`}>
        {children}
      </main>
    </div>
  );
}

const POSTHOG_WORKFLOW_FLAG = 'experiment_new-module_workflow-builder_temp';
const POSTHOG_CHATBOT_FLAG = 'experiment_new-module_chatbot-builder_temp';
const POSTHOG_PROSPECTING_FLAG = 'experiment_new-module_prospecting_temp';

function FeatureRoute({ feature, children }) {
  const { brandConfig } = useBrand();
  const workflowFlagEnabled = useFeatureFlagEnabled(POSTHOG_WORKFLOW_FLAG);
  const chatbotFlagEnabled = useFeatureFlagEnabled(POSTHOG_CHATBOT_FLAG);
  const prospectingFlagEnabled = useFeatureFlagEnabled(POSTHOG_PROSPECTING_FLAG);

  const isDisabled =
    !brandConfig.features[feature] ||
    (feature === 'workflows' && !workflowFlagEnabled) ||
    (feature === 'chatbots' && !chatbotFlagEnabled) ||
    (feature === 'prospecting' && !prospectingFlagEnabled);

  if (isDisabled) {
    // Redirect to the first available feature, falling back to /settings
    // to avoid infinite redirect loops (e.g. if chat itself is disabled).
    const featureFallbacks = [
      { key: 'chat', path: '/chat' },
      { key: 'forms', path: '/forms' },
      { key: 'pages', path: '/pages' },
    ];
    const fallback = featureFallbacks.find(
      (f) => f.key !== feature && brandConfig.features[f.key]
    );
    return <Navigate to={fallback ? fallback.path : '/settings'} replace />;
  }

  return children;
}

function AppContent() {
  const queryParams = new URLSearchParams(window.location.search);
  const embed = queryParams.get("embed");

  return (
    <Routes>
      {/* Authentication Routes - with special auth layout */}
      <Route path="/login" element={
        <AuthLayout>
          <Login />
        </AuthLayout>
      } />
      <Route path="/register" element={
        <AuthLayout>
          <Register />
        </AuthLayout>
      } />
      <Route path="/accept-invite/:token" element={
        <AuthLayout>
          <AcceptInvite />
        </AuthLayout>
      } />
      <Route path="/forgot-password" element={
        <AuthLayout>
          <ForgotPassword />
        </AuthLayout>
      } />
      <Route path="/reset-password" element={
        <AuthLayout skipRedirect>
          <ResetPassword />
        </AuthLayout>
      } />
      
      {/* Form Preview Route - loaded in iframe, no layout */}
      <Route path="/forms/:id/preview" element={<FormPreviewRenderer />} />

      {/* Public Form Routes - with minimal layout */}
      <Route path="/public/forms/:form_id" element={
        <PublicFormLayout embed={embed}>
          <PublicForm embed={embed} noAnalytics={queryParams.get('noAnalytics') === 'true'} />
        </PublicFormLayout>
      } />

      {/* Public Page Routes */}
      <Route path="/pages/:id" element={
        <PublicFormLayout embed={embed} noConstraints={true}>
          <PublicPage embed={embed} />
        </PublicFormLayout>
      } />
      <Route path="/thank-you/:form_id" element={
        <ThankYouLayout>
          <ThankYou />
        </ThankYouLayout>
      } />
      <Route path="/demo" element={
        <PublicFormLayout embed={embed}>
          <DemoPublicForm embed={embed} />
        </PublicFormLayout>
      } />
      <Route path="/embed/demo" element={
        <PublicFormLayout embed={embed}>
          <EmbedDemo />
        </PublicFormLayout>
      } />

      <Route path="/" element={<Navigate to="/chat" replace />} />
      {/* Forms Routes */}
      <Route path="/forms" element={
        <MainAppLayout>
          <PrivateRoute><FeatureRoute feature="forms">
            <FormList />
          </FeatureRoute></PrivateRoute>
        </MainAppLayout>
      } />
      <Route path="/forms/:id/builder" element={
        <MainAppLayout>
          <PrivateRoute><FeatureRoute feature="forms">
            <FormBuilder />
          </FeatureRoute></PrivateRoute>
        </MainAppLayout>
      } />
      <Route path="/forms/:id/responses" element={
        <MainAppLayout>
          <PrivateRoute><FeatureRoute feature="forms">
            <FormResponses />
          </FeatureRoute></PrivateRoute>
        </MainAppLayout>
      } />
      <Route path="/forms/:id/analytics" element={
        <MainAppLayout>
          <PrivateRoute><FeatureRoute feature="forms">
            <FormAnalytics />
          </FeatureRoute></PrivateRoute>
        </MainAppLayout>
      } />
      {/* Sites Routes */}
      <Route path="/sites" element={
        <MainAppLayout>
          <PrivateRoute><FeatureRoute feature="pages">
            <SitesList />
          </FeatureRoute></PrivateRoute>
        </MainAppLayout>
      } />
      <Route path="/sites/:siteId" element={
        <MainAppLayout>
          <PrivateRoute><FeatureRoute feature="pages">
            <SiteDetail />
          </FeatureRoute></PrivateRoute>
        </MainAppLayout>
      } />
      {/* Pages Routes (redirect /pages to /sites, keep builder) */}
      <Route path="/pages" element={<Navigate to="/sites" replace />} />
      <Route path="/pages/:id/builder" element={
        <MainAppLayout>
          <PrivateRoute><FeatureRoute feature="pages">
            <PageBuilder />
          </FeatureRoute></PrivateRoute>
        </MainAppLayout>
      } />
      {/* Data (Object) Routes — new slug-based paths */}
      <Route path="/data/:objectSlug" element={
        <MainAppLayout>
          <PrivateRoute><FeatureRoute feature="crm">
            <DataObjectSlugResolver>
              <CRMLayout />
            </DataObjectSlugResolver>
          </FeatureRoute></PrivateRoute>
        </MainAppLayout>
      }>
        <Route index element={<CRMObjectList />} />
      </Route>
      <Route path="/data/:objectSlug/import" element={
        <MainAppLayout>
          <PrivateRoute><FeatureRoute feature="crm">
            <DataObjectSlugResolver>
              <div className="px-8 py-8 max-w-[1400px] mx-auto">
                <UploadRecords />
              </div>
            </DataObjectSlugResolver>
          </FeatureRoute></PrivateRoute>
        </MainAppLayout>
      } />
      <Route path="/data/:objectSlug/:recordId" element={
        <MainAppLayout>
          <PrivateRoute><FeatureRoute feature="crm">
            <DataObjectSlugResolver>
              <CRMRecordDetail />
            </DataObjectSlugResolver>
          </FeatureRoute></PrivateRoute>
        </MainAppLayout>
      } />
      {/* Pipeline routes */}
      <Route path="/data/pipelines" element={
        <MainAppLayout>
          <PrivateRoute><FeatureRoute feature="crm">
            <CRMLayout />
          </FeatureRoute></PrivateRoute>
        </MainAppLayout>
      }>
        <Route index element={<CRMPipelines />} />
      </Route>
      <Route path="/data/pipelines/:pipelineId" element={
        <MainAppLayout>
          <PrivateRoute><FeatureRoute feature="crm">
            <CRMLayout />
          </FeatureRoute></PrivateRoute>
        </MainAppLayout>
      }>
        <Route index element={<CRMPipelineView />} />
      </Route>
      {/* Legacy CRM redirects */}
      <Route path="/crm" element={
        <MainAppLayout>
          <PrivateRoute><FeatureRoute feature="crm">
            <Navigate to="/data/companies" replace />
          </FeatureRoute></PrivateRoute>
        </MainAppLayout>
      } />
      <Route path="/crm/companies" element={<Navigate to="/data/companies" replace />} />
      <Route path="/crm/people" element={<Navigate to="/data/people" replace />} />
      <Route path="/crm/companies/:id" element={<Navigate to="/data/companies" replace />} />
      <Route path="/crm/people/:id" element={<Navigate to="/data/people" replace />} />
      <Route path="/crm/objects/:objectDefinitionId" element={
        <MainAppLayout>
          <PrivateRoute><FeatureRoute feature="crm">
            <DataObjectIdRedirect />
          </FeatureRoute></PrivateRoute>
        </MainAppLayout>
      } />
      <Route path="/crm/objects/:objectDefinitionId/records/:recordId" element={
        <MainAppLayout>
          <PrivateRoute><FeatureRoute feature="crm">
            <DataObjectIdRedirect />
          </FeatureRoute></PrivateRoute>
        </MainAppLayout>
      } />
      <Route path="/crm/pipelines" element={<Navigate to="/data/pipelines" replace />} />
      <Route path="/crm/pipelines/:pipelineId" element={<Navigate to="/data/pipelines" replace />} />
      {/* Workflow Routes */}
      <Route path="/workflows" element={
        <MainAppLayout>
          <PrivateRoute><FeatureRoute feature="workflows">
            <WorkflowList />
          </FeatureRoute></PrivateRoute>
        </MainAppLayout>
      } />
      <Route path="/workflows/:id/builder" element={
        <MainAppLayout>
          <PrivateRoute><FeatureRoute feature="workflows">
            <WorkflowBuilder />
          </FeatureRoute></PrivateRoute>
        </MainAppLayout>
      } />
      {/* Chatbot Routes */}
      <Route path="/chatbots" element={
        <MainAppLayout>
          <PrivateRoute><FeatureRoute feature="chatbots">
            <ChatbotList />
          </FeatureRoute></PrivateRoute>
        </MainAppLayout>
      } />
      <Route path="/chatbots/:id/builder" element={
        <MainAppLayout>
          <PrivateRoute><FeatureRoute feature="chatbots">
            <ChatbotBuilder />
          </FeatureRoute></PrivateRoute>
        </MainAppLayout>
      } />
      {/* Prospecting Routes — provider lives at the App root (see App component) so state
          survives SidebarLayout's location-keyed remount. */}
      <Route path="/prospecting" element={
        <MainAppLayout>
          <PrivateRoute><FeatureRoute feature="prospecting">
            <Outlet />
          </FeatureRoute></PrivateRoute>
        </MainAppLayout>
      }>
        <Route index element={<Prospecting />} />
        <Route path="new" element={<ProspectingGuidedSetup />} />
        <Route path="lists/:listId" element={<ProspectingSavedListWorkspace />} />
        <Route path=":searchId" element={<ProspectingWorkspace />} />
      </Route>
      {/* Chat Routes */}
      <Route path="/chat/:id?" element={
        <MainAppLayout>
          <PrivateRoute><FeatureRoute feature="chat">
            <ChatApp />
          </FeatureRoute></PrivateRoute>
        </MainAppLayout>
      } />
      {/* Admin Dashboard */}
      <Route path="/admin" element={
        <MainAppLayout>
          <PrivateRoute>
            <SuperAdminRoute>
              <SuperAdminDashboard />
            </SuperAdminRoute>
          </PrivateRoute>
        </MainAppLayout>
      } />
      {/* Settings - always available */}
      <Route path="/settings" element={
        <MainAppLayout>
          <PrivateRoute>
            <SettingsPage />
          </PrivateRoute>
        </MainAppLayout>
      } />
    </Routes>
  );
}

function App() {
  return (
    <PostHogProvider client={posthog}>
      <BrandProvider>
        <ThemeProvider>
          <ToastProvider>
            <AuthProvider>
              <DataCacheProvider>
                <ProspectingProvider>
                  <Router>
                    <PostHogPageviewTracker />
                    <KeyboardShortcutsProvider>
                      <AppContent />
                    </KeyboardShortcutsProvider>
                  </Router>
                </ProspectingProvider>
              </DataCacheProvider>
            </AuthProvider>
          </ToastProvider>
        </ThemeProvider>
      </BrandProvider>
    </PostHogProvider>
  );
}

export default App;
